/**
 * Pull a readable message out of an API error.
 *
 * `error.message` is only ever axios's generic "Request failed with status code
 * 422", which tells the reviewer nothing. The real reason is in the response
 * body, and FastAPI uses three different shapes for it:
 *   - HTTPException(detail={"error": {...}})  -> detail.error.message
 *   - RequestValidationError (422)            -> detail is an array of {loc, msg}
 *   - HTTPException(detail="...")             -> detail is a plain string
 * Same idea as the publish-guard handler in EnhancementsPage.tsx.
 */
export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  const axiosErr = error as {
    message?: string
    response?: {
      data?: {
        detail?:
          | string
          | { error?: { message?: string } }
          | Array<{ loc?: (string | number)[]; msg?: string }>
        error?: { message?: string }
      }
    }
  }
  const data = axiosErr?.response?.data
  const detail = data?.detail

  if (Array.isArray(detail)) {
    // Validation errors: name the offending field so the fix is obvious.
    const parts = detail
      .map((d) => {
        const field = d.loc?.filter((p) => p !== 'body').join('.')
        return field ? `${field}: ${d.msg}` : d.msg
      })
      .filter(Boolean)
    if (parts.length) return parts.join('; ')
  }
  if (typeof detail === 'string' && detail) return detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.error?.message) {
    return detail.error.message
  }
  if (data?.error?.message) return data.error.message
  return axiosErr?.message || fallback
}
